import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { OrdersUtilsService } from '@app/orders/services/orders-utils.service';
import { OrdersTreeListUtilsService } from '@app/orders/services/orders-tree-utils.service';
import { OrdersService } from '@app/orders/services/orders.service';
import { DestroyableComponent } from '@core/components/destroyable/destroyable.component';
import { AlertService } from '@core/services/alert.service';
import {
  ApiException,
  ExportOrderItemTypeEnumRequestDto,
  ExportOrderPropertyEnumRequestDto,
  ExportOrdersToCsvRequest,
  FileResponse,
  OrderByDirectionEnum,
  OrderItemPropertyFilterByEnumRequestDto,
  OrderItemRowTypeEnumRequestDto,
  OrderItemTypeEnumRequestDto,
  OrderPropertyEnumRequestDto,
  PropertyFilterOfOrderPropertyEnumRequestDtoAndOperatorTypeEnumRequestDto,
} from '@core/services/apiclient';
import { Store } from '@ngrx/store';
import * as fromOrders from '@root-store/orders-store/index';
import { Utils } from '@shared/utils/utils';
import { Constants } from '@core/app-constants';

@Component({
  selector: 'app-orders-header',
  templateUrl: './orders-header.component.html',
  styleUrls: ['./orders-header.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrdersHeaderComponent extends DestroyableComponent {
  private readonly store = inject<Store<fromOrders.State>>(Store);
  private readonly ordersUtils = inject(OrdersUtilsService);
  private readonly treeListUtils = inject(OrdersTreeListUtilsService);
  private readonly orderService = inject(OrdersService);
  private readonly utils = inject(Utils);
  private readonly alertService = inject(AlertService);
  readonly resultInfo = this.treeListUtils.resultInfo;
  readonly totalCount = computed(() => this.resultInfo()?.totalCount ?? 0);
  readonly ordersFilters = this.store.selectSignal(fromOrders.selectors.selectOrderItemsFilters);
  readonly isDefaultOrdersList = this.store.selectSignal(fromOrders.selectors.selectIsDefaultOrdersList);
  readonly maxExport = Constants.MaxExportCount;

  public getTooltip() {
    const totalCount = this.totalCount();
    if (totalCount === 0) {
      return 'orders.export.noResultsToExport';
    } else if (totalCount > this.maxExport) {
      return 'orders.export.maximumNumberOfResultsToExport';
    }
  }

  public exportOrders() {
    this.orderService.exportOrderItems(this.getPayloadData()).subscribe({
      next: (value: FileResponse) => {
        this.utils.saveToFileSystem(value.data, value?.fileName, value?.headers?.['content-type']);
        this.alertService.showCustom('orders.export.exportIsSuccessfullyDownloaded', 'success');
      },
      error: (error: unknown) => {
        // showApiError surfaces the traceId for 4xx, and defers 500s to the global
        // ErrorInterceptor (which also surfaces the traceId) — see Frontend/docs/error-handling.md.
        const fallback =
          error instanceof ApiException && error.status === 403
            ? 'orders.export.notAuthorizedToExport'
            : 'orders.export.problemOccurredWhileExporting';
        this.alertService.showApiError(error, fallback);
      },
    });
  }

  // TreeList filter fields missing from OrderPropertyEnumRequestDto must be mapped here,
  // otherwise the export endpoint rejects the payload (enum binding failure -> 400).
  private static readonly TREE_LIST_PROPERTY_MAP = new Map<string, OrderPropertyEnumRequestDto>([
    [OrderItemPropertyFilterByEnumRequestDto.SalesUserIdentifier, OrderPropertyEnumRequestDto.OperatorIdentifier],
    [OrderItemPropertyFilterByEnumRequestDto.EventSessionIdentifier, OrderPropertyEnumRequestDto.SessionIdentifier],
  ]);

  // L'export vit dans le module Orders (namespace propre) : ses enums sont distincts de ceux de la
  // recherche (Sales). Les valeurs string sont identiques, on convertit donc recherche -> export par
  // valeur (lookup runtime, pas de cast).
  private static readonly EXPORT_PROPERTY_BY_VALUE: Record<string, ExportOrderPropertyEnumRequestDto> =
    Object.fromEntries(Object.values(ExportOrderPropertyEnumRequestDto).map((value) => [value, value]));
  private static readonly EXPORT_ITEM_TYPE_BY_VALUE: Record<string, ExportOrderItemTypeEnumRequestDto> =
    Object.fromEntries(Object.values(ExportOrderItemTypeEnumRequestDto).map((value) => [value, value]));

  private getPayloadData(): ExportOrdersToCsvRequest {
    const filters = this.ordersFilters() ?? [];
    const criterias = this.mapCriteriasForExport(this.ordersUtils.buildCriterias(filters));
    return {
      includes: this.getTypesForExport().map((type) => this.toExportItemType(type)),
      criterias: criterias.map((criteria) => ({
        property: this.toExportProperty(criteria.property),
        condition: criteria.condition,
        value: criteria.value,
      })),
      // Le tri de la TreeList n'est pas remonte au store : l'export part donc du tri par defaut.
      orderBy: {
        direction: OrderByDirectionEnum.Desc,
        property: ExportOrderPropertyEnumRequestDto.BookingDate,
      },
      isDefault: this.isDefaultOrdersList() ?? !filters.length,
    };
  }

  private toExportProperty(property: OrderPropertyEnumRequestDto): ExportOrderPropertyEnumRequestDto {
    const mapped = OrdersHeaderComponent.EXPORT_PROPERTY_BY_VALUE[property];
    if (mapped === undefined) {
      throw new Error(`Unknown export order property: ${property}`);
    }
    return mapped;
  }

  private toExportItemType(type: OrderItemTypeEnumRequestDto): ExportOrderItemTypeEnumRequestDto {
    const mapped = OrdersHeaderComponent.EXPORT_ITEM_TYPE_BY_VALUE[type];
    if (mapped === undefined) {
      throw new Error(`Unknown export order item type: ${type}`);
    }
    return mapped;
  }

  private mapCriteriasForExport(
    criterias: PropertyFilterOfOrderPropertyEnumRequestDtoAndOperatorTypeEnumRequestDto[],
  ): PropertyFilterOfOrderPropertyEnumRequestDtoAndOperatorTypeEnumRequestDto[] {
    return criterias.map((c) => ({
      ...c,
      property: OrdersHeaderComponent.TREE_LIST_PROPERTY_MAP.get(c.property) ?? c.property,
    }));
  }

  private getTypesForExport(): OrderItemTypeEnumRequestDto[] {
    const orderItemTypes = this.treeListUtils
      .itemRowTypes()
      .map((type) => {
        if (type === OrderItemRowTypeEnumRequestDto.EventSession) {
          return OrderItemTypeEnumRequestDto.Session;
        }
        if (type === OrderItemRowTypeEnumRequestDto.Product) {
          return OrderItemTypeEnumRequestDto.Product;
        }
        return null;
      })
      .filter((type): type is OrderItemTypeEnumRequestDto => type !== null);

    return [...new Set(orderItemTypes)];
  }
}
